/**
 * LoyaltyPage — Partner VIP lojalitasi program dashboard
 * Epic 68: VIP Lojalitási Program
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useEarnPoints, useLoyaltyRewards, usePartnerLoyalty } from '@/hooks/useLoyalty';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

// SOURCE_LABELS moved inside component for i18n

export function LoyaltyPage() {
  const { t } = useTranslation();
  const SOURCE_LABELS: Record<string, string> = {
    RENTAL: t('loyalty.sourceLabelsObj.RENTAL'),
    PURCHASE: t('loyalty.sourceLabelsObj.PURCHASE'),
    SERVICE: t('loyalty.sourceLabelsObj.SERVICE'),
    SALES: t('loyalty.sourceLabelsObj.SALES'),
    BONUS: t('loyalty.sourceLabelsObj.BONUS'),
    REDEMPTION: t('loyalty.sourceLabelsObj.REDEMPTION'),
    EXPIRY: t('loyalty.sourceLabelsObj.EXPIRY'),
  };
  const [partnerId, setPartnerId] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | undefined>(undefined);

  const { status, isLoading, error, refetch } = usePartnerLoyalty(selectedPartnerId);
  const { rewards } = useLoyaltyRewards();
  const earnPoints = useEarnPoints(selectedPartnerId ?? '');

  const [earnForm, setEarnForm] = useState({
    amount: '',
    source: 'PURCHASE' as 'RENTAL' | 'PURCHASE' | 'SERVICE' | 'SALES' | 'BONUS',
    // M-016: rentalCount removed — server-computed from DB rental records
    note: '',
  });

  const handleSearch = () => {
    if (partnerId) {
      setSelectedPartnerId(partnerId);
    }
  };

  const handleEarn = async () => {
    if (!selectedPartnerId) return;
    const amount = parseInt(earnForm.amount, 10);
    if (!amount || amount <= 0) return;

    await earnPoints.mutateAsync({
      amount,
      source: earnForm.source,
      // M-016: rentalCount is now server-computed, not sent from client
      note: earnForm.note || undefined,
    });

    setEarnForm({ amount: '', source: 'PURCHASE', note: '' });
    refetch();
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('loyalty.title')}</h1>

      {/* Partner search */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Input
              placeholder="Partner ID"
              value={partnerId}
              onChange={e => setPartnerId(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSearch}>{t('loyalty.searchButton')}</Button>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="text-center py-8 text-gray-500 dark:text-[#ccfbf1]">
          {t('loyalty.loading')}
        </div>
      )}

      {error && (
        <Card className="mb-6">
          <CardContent className="p-8 text-center text-red-500">
            {t('common.errorLabel')}: {error}
          </CardContent>
        </Card>
      )}

      {status && selectedPartnerId && (
        <>
          {/* Balance & trusted status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-l-4 border-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{t('loyalty.pointBalance')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {status.balance} {t('loyalty.points')}
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{t('loyalty.trustedStatus')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${status.trusted?.isTrusted ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-[#ccfbf1]'}`}
                >
                  {status.trusted?.isTrusted ? t('loyalty.trusted') : t('loyalty.notTrusted')}
                </div>
                {status.trusted && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {t('loyalty.consecutiveCleanRentals', {
                      count: status.trusted.consecutiveCleanRentals,
                    })}
                    {status.trusted.isTrusted && ` — ${t('loyalty.depositFree')}!`}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-l-4 border-purple-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{t('loyalty.depositTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {status.trusted ? formatCurrency(status.trusted.depositAmount) : 'N/A'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Earn points form */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('loyalty.creditPoints')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Input
                  type="number"
                  placeholder={t('loyalty.amountPlaceholder')}
                  value={earnForm.amount}
                  onChange={e => setEarnForm({ ...earnForm, amount: e.target.value })}
                />
                <select
                  className="border rounded px-3 py-2 bg-background"
                  value={earnForm.source}
                  onChange={e =>
                    setEarnForm({ ...earnForm, source: e.target.value as typeof earnForm.source })
                  }
                >
                  <option value="PURCHASE">{t('loyalty.sourceLabelsObj.PURCHASE')}</option>
                  <option value="RENTAL">{t('loyalty.sourceLabelsObj.RENTAL')}</option>
                  <option value="SERVICE">{t('loyalty.sourceLabelsObj.SERVICE')}</option>
                  <option value="SALES">{t('loyalty.sourceLabelsObj.SALES')}</option>
                  <option value="BONUS">{t('loyalty.sourceLabelsObj.BONUS')}</option>
                </select>
                {/* M-016: rentalCount input removed — server-computed from DB */}
                <Input
                  placeholder={t('loyalty.notePlaceholder')}
                  value={earnForm.note}
                  onChange={e => setEarnForm({ ...earnForm, note: e.target.value })}
                />
                <Button onClick={handleEarn} disabled={earnPoints.isPending}>
                  {earnPoints.isPending ? t('loyalty.crediting') : t('loyalty.creditButton')}
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-[#ccfbf1]mt-2">
                {t('loyalty.calculationInfo')}
              </p>
            </CardContent>
          </Card>

          {/* Available rewards */}
          {rewards.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('loyalty.redeemableRewards')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y dark:divide-[#2d7d8a]">
                  {rewards.map(reward => (
                    <div key={reward.id} className="py-3 flex justify-between items-center">
                      <div>
                        <span className="font-medium">{reward.name}</span>
                        <span className="ml-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
                          {reward.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">
                          {reward.pointCost} {t('loyalty.points')}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={status.balance < reward.pointCost}
                        >
                          {t('loyalty.redeemButton')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Point history */}
          <Card>
            <CardHeader>
              <CardTitle>
                {t('loyalty.pointHistory', { count: '' })} ({status.history.length} db)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {status.history.length === 0 ? (
                <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
                  {t('loyalty.noPointRecords')}.
                </p>
              ) : (
                <div className="divide-y dark:divide-[#2d7d8a]">
                  {status.history.map(point => (
                    <div key={point.id} className="py-3 flex justify-between items-center">
                      <div>
                        <span
                          className={`font-medium ${point.points >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {point.points >= 0 ? '+' : ''}
                          {point.points} {t('loyalty.points')}
                        </span>
                        <span className="ml-3 text-sm text-gray-500 dark:text-[#ccfbf1]">
                          {SOURCE_LABELS[point.source] ?? point.source}
                        </span>
                        {point.note && (
                          <span className="ml-2 text-sm text-gray-400 dark:text-[#99f6e4]">
                            — {point.note}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400 dark:text-[#99f6e4]">
                          {new Date(point.createdAt).toLocaleDateString('hu-HU')}
                        </div>
                        {point.expiresAt && (
                          <div className="text-xs text-orange-500">
                            {t('loyalty.expiresLabel')}:{' '}
                            {new Date(point.expiresAt).toLocaleDateString('hu-HU')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
